import {
  BrowseChannelsViewModel,
  ChannelMembersViewModel,
  type ChannelMembersState,
  ChannelSettingsViewModel,
  CreateChannelViewModel,
  NewMessageViewModel,
  type NewMessageState,
  type BrowseChannelsState,
  type ChannelSettingsState,
  type CreateChannelState,
} from "@bundle/core";
import { useCallback, useSyncExternalStore } from "react";
import { useWorkspaceContext } from "../context/AppContext.tsx";
import { useOwned } from "./owned.ts";

/** The "Create channel" form of this workspace (one per workspace; `reset()` when it opens). */
export function useCreateChannel(): { vm: CreateChannelViewModel; state: CreateChannelState } {
  const { workspace } = useWorkspaceContext();
  const create = useCallback(() => new CreateChannelViewModel(workspace), [workspace]);
  const vm = useOwned(`createChannel:${workspace.wsId}:${workspace.state.meId}`, create);
  return { vm, state: useSyncExternalStore(vm.subscribe, vm.getSnapshot, vm.getSnapshot) };
}

/** "Browse channels" of this workspace. */
export function useBrowseChannels(): { vm: BrowseChannelsViewModel; state: BrowseChannelsState } {
  const { workspace } = useWorkspaceContext();
  const create = useCallback(() => new BrowseChannelsViewModel(workspace), [workspace]);
  const vm = useOwned(`browseChannels:${workspace.wsId}:${workspace.state.meId}`, create);
  return { vm, state: useSyncExternalStore(vm.subscribe, vm.getSnapshot, vm.getSnapshot) };
}

/** Settings of one channel (shared by everything showing it). */
export function useChannelSettings(channelId: string): { vm: ChannelSettingsViewModel; state: ChannelSettingsState } {
  const { workspace } = useWorkspaceContext();
  const create = useCallback(() => new ChannelSettingsViewModel(workspace, channelId), [workspace, channelId]);
  const vm = useOwned(`channelSettings:${workspace.wsId}:${workspace.state.meId}:${channelId}`, create);
  return { vm, state: useSyncExternalStore(vm.subscribe, vm.getSnapshot, vm.getSnapshot) };
}

/** The members panel of one channel. */
export function useChannelMembers(channelId: string): { vm: ChannelMembersViewModel; state: ChannelMembersState } {
  const { workspace } = useWorkspaceContext();
  const create = useCallback(() => new ChannelMembersViewModel(workspace, channelId), [workspace, channelId]);
  const vm = useOwned(`channelMembers:${workspace.wsId}:${workspace.state.meId}:${channelId}`, create);
  return { vm, state: useSyncExternalStore(vm.subscribe, vm.getSnapshot, vm.getSnapshot) };
}

/** "New message" of this workspace (one per workspace; `reset(prefill)` when it opens). */
export function useNewMessage(): { vm: NewMessageViewModel; state: NewMessageState } {
  const { workspace } = useWorkspaceContext();
  const create = useCallback(() => new NewMessageViewModel(workspace), [workspace]);
  const vm = useOwned(`newMessage:${workspace.wsId}:${workspace.state.meId}`, create);
  return { vm, state: useSyncExternalStore(vm.subscribe, vm.getSnapshot, vm.getSnapshot) };
}
